import { useEffect, useState } from "react";
import {
  useGetProductsQuery,
  useGetCustomersQuery,
  useGetInvoiceQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
} from "../../../../redux/api";
import { IconPlus, IconTrash } from "../../../../components/icons";
import { Modal } from "../../../../components/Modal";
import { EntityMeta } from "../../../../components/EntityMeta";
import { statusTone } from "../../../../utils/status";
import { Field } from "../../../../components/Field";

interface InvoiceFormModalProps {
  open: boolean;
  /** null = create */
  id: string | null;
  onClose: () => void;
}

type ItemRow = {
  productId: string;
  productName: string;
  quantity: string;
  unitPrice: string;
};

const emptyRow: ItemRow = {
  productId: "",
  productName: "",
  quantity: "1",
  unitPrice: "",
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const n = (v: string) => Number(v) || 0;

export const InvoiceFormModal = ({ open, id, onClose }: InvoiceFormModalProps) => {
  const [activeId, setActiveId] = useState<string | null>(id);
  const isEdit = !!activeId;

  const { data: existing } = useGetInvoiceQuery(activeId ?? "", {
    skip: !activeId,
  });


  const { data: products = [] } = useGetProductsQuery();
  const { data: customers = [] } = useGetCustomersQuery();

  const [createInvoice, { isLoading: creating }] = useCreateInvoiceMutation();
  const [updateInvoice, { isLoading: updating }] = useUpdateInvoiceMutation();

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [status, setStatus] = useState("draft");
  const [taxPercentage, setTaxPercentage] = useState("0");
  const [amountPaid, setAmountPaid] = useState("0");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<ItemRow[]>([{ ...emptyRow }]);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const saving = creating || updating;

  // Re-sync to the record being opened every time the popup opens (or the
  // target id changes while open) — this component stays mounted between
  // opens, so state from a previous visit would otherwise linger.
  useEffect(() => {
    if (!open) return;
    setActiveId(id);
    setFormError(null);
    setFieldErrors({});
    if (id && existing && existing.id === id) {
      setInvoiceNumber(existing.invoiceNumber ?? "");
      setInvoiceDate((existing.invoiceDate ?? todayISO()).slice(0, 10));
      setDueDate((existing.dueDate ?? "").slice(0, 10));
      setCustomerId(existing.customerId ?? "");
      setStatus(existing.status ?? "draft");
      setTaxPercentage(
        existing.taxPercentage != null ? String(existing.taxPercentage) : "0",
      );
      setAmountPaid(existing.amountPaid != null ? String(existing.amountPaid) : "0");
      setNotes(existing.notes ?? "");
      setItems(
        (existing.items ?? []).map((it) => ({
          productId: it.productId,
          productName: it.productName ?? "",
          quantity: String(it.quantity),
          unitPrice: String(it.unitPrice),
        })),
      );
    } else if (!id) {
      setInvoiceNumber("");
      setInvoiceDate(todayISO());
      setDueDate("");
      setCustomerId("");
      setStatus("draft");
      setTaxPercentage("0");
      setAmountPaid("0");
      setNotes("");
      setItems([{ ...emptyRow }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  useEffect(() => {
    if (!existing) return;
    setInvoiceNumber(existing.invoiceNumber ?? "");
    setInvoiceDate((existing.invoiceDate ?? todayISO()).slice(0, 10));
    setDueDate((existing.dueDate ?? "").slice(0, 10));
    setCustomerId(existing.customerId ?? "");
    setStatus(existing.status ?? "draft");
    setTaxPercentage(
      existing.taxPercentage != null ? String(existing.taxPercentage) : "0",
    );
    setAmountPaid(existing.amountPaid != null ? String(existing.amountPaid) : "0");
    setNotes(existing.notes ?? "");
    setItems(
      (existing.items ?? []).map((it) => ({
        productId: it.productId,
        productName: it.productName ?? "",
        quantity: String(it.quantity),
        unitPrice: String(it.unitPrice),
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updatedAt]);

  const setItem = (index: number, patch: Partial<ItemRow>) =>
    setItems((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );

  const onPickProduct = (index: number, productId: string) => {
    const product = products.find((p) => p.id === productId);
    setItem(index, {
      productId,
      productName: product?.productName ?? "",
      unitPrice:
        product && !items[index].unitPrice
          ? String(product.sellingPrice ?? 0)
          : items[index].unitPrice,
    });
  };

  const addRow = () => setItems((prev) => [...prev, { ...emptyRow }]);
  const removeRow = (index: number) =>
    setItems((prev) => prev.filter((_, i) => i !== index));

  const lineTotal = (row: ItemRow) => n(row.quantity) * n(row.unitPrice);
  const subtotal = items.reduce((sum, row) => sum + lineTotal(row), 0);
  const taxAmount = (subtotal * n(taxPercentage)) / 100;
  const total = subtotal + taxAmount;
  const balance = total - n(amountPaid);

  const handleSave = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!invoiceNumber.trim()) errors.invoiceNumber = "Invoice number is required.";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const cleanItems = items
      .filter((row) => row.productId)
      .map((row) => ({
        productId: row.productId,
        productName: row.productName || undefined,
        quantity: n(row.quantity),
        unitPrice: n(row.unitPrice),
      }));

    if (cleanItems.length === 0) {
      setFormError("Add at least one line item.");
      return;
    }

    const payload = {
      invoiceNumber: invoiceNumber.trim(),
      invoiceDate,
      dueDate: dueDate || undefined,
      customerId: customerId || undefined,
      taxPercentage: n(taxPercentage),
      amountPaid: n(amountPaid),
      status,
      notes: notes.trim() || undefined,
      items: cleanItems,
    };

    try {
      if (isEdit && activeId) {
        await updateInvoice({ id: activeId, input: payload }).unwrap();
      } else {
        await createInvoice({ ...payload }).unwrap();
      }
      onClose();
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save invoice.",
      );
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit invoice" : "New invoice"}
      onClose={onClose}
      xwide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create invoice"}
          </button>
        </>
      }
    >
      <EntityMeta
        id={activeId ?? undefined}
        status={isEdit ? { label: status, tone: statusTone(status) } : undefined}
      />

      {formError ? <div className="form-error">{formError}</div> : null}

      <div className="form-grid">
        <Field label="Invoice number *" error={fieldErrors.invoiceNumber}>
          <input
            value={invoiceNumber}
            onChange={(e) => {
              setInvoiceNumber(e.target.value);
              setFieldErrors((prev) => ({ ...prev, invoiceNumber: "" }));
            }}
            placeholder="INV-001"
          />
        </Field>
        <label className="field">
          Customer
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
          >
            <option value="">— None —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Invoice date
          <input
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
          />
        </label>
        <label className="field">
          Due date
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </label>
        <label className="field">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>
        <label className="field">
          Tax %
          <input
            type="number"
            value={taxPercentage}
            onChange={(e) => setTaxPercentage(e.target.value)}
          />
        </label>
      </div>

      <div className="panel__head" style={{ marginTop: "1.5rem" }}>
        <div>
          <h2>Line items</h2>
          <p className="panel__sub">{items.length} row(s)</p>
        </div>
        <button type="button" className="btn" onClick={addRow}>
          <IconPlus /> Add item
        </button>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: "40%" }}>Product</th>
              <th>Qty</th>
              <th>Unit price</th>
              <th>Line total</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td className="data-table__empty" colSpan={5}>
                  No items. Click “Add item”.
                </td>
              </tr>
            ) : (
              items.map((row, index) => (
                <tr key={index}>
                  <td>
                    <select
                      className="cell-input"
                      value={row.productId}
                      onChange={(e) => onPickProduct(index, e.target.value)}
                    >
                      <option value="">— Select product —</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.productName} ({p.productCode})
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      className="cell-input"
                      type="number"
                      value={row.quantity}
                      onChange={(e) =>
                        setItem(index, { quantity: e.target.value })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="cell-input"
                      type="number"
                      value={row.unitPrice}
                      onChange={(e) =>
                        setItem(index, { unitPrice: e.target.value })
                      }
                    />
                  </td>
                  <td className="cell-date">
                    {lineTotal(row).toLocaleString()}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label="Remove item"
                      onClick={() => removeRow(index)}
                    >
                      <IconTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="totals">
        <div>
          <span>Subtotal</span>
          <strong>{subtotal.toLocaleString()}</strong>
        </div>
        <div>
          <span>Tax ({n(taxPercentage)}%)</span>
          <strong>{taxAmount.toLocaleString()}</strong>
        </div>
        <div className="totals__grand">
          <span>Total</span>
          <strong>{total.toLocaleString()}</strong>
        </div>
        <div>
          <span>Amount paid</span>
          <strong>
            <input
              type="number"
              className="cell-input"
              style={{ width: 120, textAlign: "right" }}
              value={amountPaid}
              onChange={(e) => setAmountPaid(e.target.value)}
            />
          </strong>
        </div>
        <div className="totals__grand">
          <span>Balance due</span>
          <strong>{balance.toLocaleString()}</strong>
        </div>
      </div>

      <label className="field field--full" style={{ marginTop: "1rem" }}>
        Notes
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
    </Modal>
  );
};
